
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { initializeGoogleMapsServices, validateAndGeocodeAddress } from '@/lib/google-maps';

interface AddressSearchOnlyProps {
  onLocationSelect: (location: { address: string; lat: number; lng: number }) => void;
  initialLocation?: { lat: number; lng: number; address?: string };
}

interface AddressSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface GeocodedAddress {
  address: string;
  lat: number;
  lng: number;
  formatted_address: string;
  place_id: string;
  types: string[];
  validity: 'valid' | 'invalid' | 'partial';
}

export const AddressSearchOnly: React.FC<AddressSearchOnlyProps> = ({
  onLocationSelect,
  initialLocation,
}) => {
  const [address, setAddress] = useState(initialLocation?.address || '');
  const [coordinates, setCoordinates] = useState(
    initialLocation || { lat: 46.8139, lng: -71.2080 } // Québec par défaut
  );
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<GeocodedAddress | null>(null);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoder = useRef<google.maps.Geocoder | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Initialiser Google Maps Services
    const initServices = async () => {
      try {
        const services = await initializeGoogleMapsServices();
        autocompleteService.current = services.autocompleteService;
        geocoder.current = services.geocoder;
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de Google Maps:', error);
      }
    };
    
    initServices();
  }, []);

  // Recherche d'autocomplétion
  const searchAddressSuggestions = async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!autocompleteService.current) {
      console.warn('Service Google Maps non initialisé');
      return;
    }

    try {
      setIsSearching(true);
      
      const request = {
        input: query,
        componentRestrictions: { country: 'ca' }, // Limiter au Canada
        types: ['address', 'establishment', 'geocode'],
      };

      autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          const formattedSuggestions: AddressSuggestion[] = predictions.map(prediction => ({
            place_id: prediction.place_id,
            description: prediction.description,
            structured_formatting: {
              main_text: prediction.structured_formatting?.main_text || '',
              secondary_text: prediction.structured_formatting?.secondary_text || '',
            },
          }));
          
          setSuggestions(formattedSuggestions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
        setIsSearching(false);
      });
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresse:', error);
      setSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
    }
  };

  // Géocodage d'une adresse
  const geocodeAddress = async (placeId: string): Promise<GeocodedAddress | null> => {
    if (!geocoder.current) {
      console.warn('Géocodeur Google Maps non initialisé');
      return null;
    }

    try {
      const request = { placeId };
      const response = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.current!.geocode(request, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results) {
            resolve(results);
          } else {
            reject(new Error(`Erreur de géocodage: ${status}`));
          }
        });
      });

      if (response && response.length > 0) {
        const result = response[0];
        const location = result.geometry?.location;
        
        if (location) {
          const geocodedAddress: GeocodedAddress = {
            address: result.formatted_address || '',
            lat: location.lat(),
            lng: location.lng(),
            formatted_address: result.formatted_address || '',
            place_id: result.place_id || '',
            types: result.types || [],
            validity: determineAddressValidity(result),
          };

          return geocodedAddress;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Erreur lors du géocodage:', error);
      return null;
    }
  };

  // Déterminer la validité de l'adresse
  const determineAddressValidity = (result: google.maps.GeocoderResult): 'valid' | 'invalid' | 'partial' => {
    const types = result.types || [];
    const hasStreetAddress = types.includes('street_address') || types.includes('route');
    const hasLocality = types.includes('locality') || types.includes('administrative_area_level_1');
    
    if (hasStreetAddress && hasLocality) {
      return 'valid';
    } else if (hasLocality) {
      return 'partial';
    } else {
      return 'invalid';
    }
  };

  // Sélection d'une suggestion
  const selectSuggestion = async (suggestion: AddressSuggestion) => {
    try {
      setValidationStatus('validating');
      setShowSuggestions(false);
      
      const geocodedAddress = await geocodeAddress(suggestion.place_id);
      
      if (geocodedAddress) {
        setSelectedAddress(geocodedAddress);
        setAddress(geocodedAddress.formatted_address);
        setCoordinates({ lat: geocodedAddress.lat, lng: geocodedAddress.lng });
        setValidationStatus(geocodedAddress.validity === 'valid' ? 'valid' : 'partial');
        
        // Notifier le composant parent
        onLocationSelect({
          address: geocodedAddress.formatted_address,
          lat: geocodedAddress.lat,
          lng: geocodedAddress.lng,
        });
      } else {
        setValidationStatus('invalid');
      }
    } catch (error) {
      console.error('Erreur lors de la sélection de l\'adresse:', error);
      setValidationStatus('invalid');
    }
  };

  // Gestion du changement d'adresse
  const handleAddressChange = (value: string) => {
    setAddress(value);
    setValidationStatus('idle');
    setSelectedAddress(null);
    
    // Debounce la recherche
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    searchTimeout.current = setTimeout(() => {
      searchAddressSuggestions(value);
    }, 300);
  };

  // Validation manuelle d'une adresse
  const validateCurrentAddress = async () => {
    if (!address.trim()) return;
    
    try {
      setValidationStatus('validating');
      
      const validationResult = await validateAndGeocodeAddress(address);
      
      if (validationResult.isValid && validationResult.coordinates) {
        const geocodedAddress: GeocodedAddress = {
          address: validationResult.formattedAddress,
          lat: validationResult.coordinates.lat,
          lng: validationResult.coordinates.lng,
          formatted_address: validationResult.formattedAddress,
          place_id: '',
          types: [],
          validity: validationResult.confidence === 'high' ? 'valid' : 'partial',
        };

        setSelectedAddress(geocodedAddress);
        setCoordinates(validationResult.coordinates);
        setValidationStatus(geocodedAddress.validity === 'valid' ? 'valid' : 'partial');
        
        onLocationSelect({
          address: validationResult.formattedAddress,
          lat: validationResult.coordinates.lat,
          lng: validationResult.coordinates.lng,
        });
      } else {
        setValidationStatus('invalid');
      }
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      setValidationStatus('invalid');
    }
  };

  // Nettoyer le timeout
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Recherche et validation d'adresse
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 relative">
          <Label htmlFor="address-search">Adresse du projet</Label>
          <div className="flex gap-2">
            <Input
              id="address-search"
              placeholder="Entrez une adresse complète..."
              value={address}
              onChange={(e) => handleAddressChange(e.target.value)}
              onFocus={() => address.length >= 3 && setShowSuggestions(true)}
              className="flex-1"
            />
            <Button
              type="button"
              onClick={validateCurrentAddress}
              disabled={!address.trim() || validationStatus === 'validating'}
              variant="outline"
              size="sm"
            >
              {validationStatus === 'validating' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Valider'
              )}
            </Button>
          </div>
          
          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.place_id}-${index}`}
                  type="button"
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 border-b last:border-b-0 focus:outline-none focus:bg-gray-100"
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <div className="font-medium">{suggestion.structured_formatting.main_text}</div>
                  {suggestion.structured_formatting.secondary_text && (
                    <div className="text-sm text-gray-600">{suggestion.structured_formatting.secondary_text}</div>
                  )}
                </button>
              ))}
            </div>
          )}
          
          {isSearching && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-md shadow-lg z-50 p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Recherche en cours...
              </div>
            </div>
          )}
        </div>
        
        {/* Statut de validation */}
        {validationStatus !== 'idle' && (
          <div className={`p-4 rounded-lg border ${
            validationStatus === 'valid' 
              ? 'bg-green-50 border-green-200' 
              : validationStatus === 'partial'
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {validationStatus === 'valid' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : validationStatus === 'partial' ? (
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              <h4 className={`font-medium ${
                validationStatus === 'valid' 
                  ? 'text-green-800' 
                  : validationStatus === 'partial'
                  ? 'text-yellow-800'
                  : 'text-red-800'
              }`}>
                {validationStatus === 'valid' 
                  ? 'Adresse validée' 
                  : validationStatus === 'partial'
                  ? 'Adresse partiellement validée'
                  : 'Adresse invalide'
                }
              </h4>
            </div>
            
            {selectedAddress && (
              <div className="mt-2 space-y-1">
                <p className={`text-sm ${
                  validationStatus === 'valid' 
                    ? 'text-green-600' 
                    : validationStatus === 'partial'
                    ? 'text-yellow-600'
                    : 'text-red-600'
                }`}>
                  {selectedAddress.formatted_address}
                </p>
                <p className="text-xs text-gray-500">
                  Coordonnées: {selectedAddress.lat.toFixed(6)}, {selectedAddress.lng.toFixed(6)}
                </p>
                {validationStatus === 'partial' && (
                  <p className="text-xs text-yellow-600">
                    ⚠️ L'adresse est approximative. Vérifiez les détails.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Instructions */}
        <div className="text-xs text-gray-500 space-y-1">
          <p>💡 <strong>Conseils pour une adresse valide :</strong></p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>Incluez le numéro de rue et le nom de la rue</li>
            <li>Ajoutez la ville et la province</li>
            <li>Utilisez le code postal si possible</li>
            <li>Exemple : "1234 Rue Principale, Québec, QC H2X 1L1"</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
